import React from 'react';
import { Box, TextField, IconButton } from '@mui/material';
import {
  DragIndicator as DragHandleIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

interface SortableItemEditingViewProps {
  setNodeRef: (element: HTMLElement | null) => void;
  columnKey: string;
  style: React.CSSProperties;
  attributes: Record<string, any>;
  listeners: SyntheticListenerMap | undefined;
  editingName?: string;
  onEditingNameChange?: (val: string) => void;
  onSaveRename?: () => void;
  onCancelRename?: () => void;
  renameError?: string | null;
}

export const SortableItemEditingView: React.FC<SortableItemEditingViewProps> = React.memo(({
  setNodeRef,
  columnKey,
  style,
  attributes,
  listeners,
  editingName,
  onEditingNameChange,
  onSaveRename,
  onCancelRename,
  renameError,
}) => {
  return (
    <Box
      ref={setNodeRef}
      id={`column-item-${columnKey}`}
      style={style}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        p: 1.5,
        mb: 1,
        border: '1.5px solid #2563EB',
        borderRadius: 1.5,
        backgroundColor: '#FFFFFF',
        boxShadow: '0 2px 8px rgba(37, 99, 235, 0.12)',
        gap: 1,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          touchAction: 'none',
          p: 0.5,
          m: -0.5,
          height: 36,
          borderRadius: 0.5,
          flexShrink: 0,
          '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
        }}
        title="Drag to reorder"
      >
        <DragHandleIcon fontSize="small" />
      </Box>

      <Box
        sx={{ flex: 1, minWidth: 0 }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          value={editingName || ''}
          onChange={(e) => onEditingNameChange?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onSaveRename?.();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancelRename?.();
            }
          }}
          autoFocus
          placeholder="New Column Name"
          error={Boolean(renameError)}
          helperText={
            renameError
              ? renameError
              : (editingName || '').length >= 25
              ? 'Name cannot exceed 25 characters.'
              : undefined
          }
          inputProps={{ maxLength: 25 }}
          FormHelperTextProps={{
            sx: {
              margin: '4px 0 0 !important',
              fontSize: '0.75rem',
              color: renameError
                ? 'error.main'
                : (editingName || '').length >= 25
                ? 'warning.main'
                : 'text.secondary',
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              height: 36,
              fontSize: '0.875rem',
              bgcolor: 'background.paper',
            },
          }}
        />
      </Box>

      <Box
        sx={{ display: 'flex', gap: 0.5, flexShrink: 0, height: 36, alignItems: 'center' }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <IconButton
          size="small"
          onClick={onSaveRename}
          disabled={!editingName?.trim() || Boolean(renameError)}
          sx={{
            color: 'primary.main',
            '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.08)' },
          }}
          title="Save name"
        >
          <SaveIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onCancelRename}
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
          }}
          title="Cancel editing"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
});

SortableItemEditingView.displayName = 'SortableItemEditingView';



